import { lazy } from 'react';

import { adminPaths } from 'routes/constants/adminPaths';
import { orgOnboardingRoutePatterns } from 'routes/constants/orgOnboardingPaths';

import { Navigate, Route, useNavigate } from 'react-router-dom';

import { getChallengeSession, hasAuthTokens } from 'services/auth';

import AdminPrivateRoute from './guards/AdminPrivateRoute';
import AdminPublicRoute from './guards/AdminPublicRoute';
import {
  OrganizationUnitEmployeesPathRedirect,
  OrganizationUnitLegacyPathRedirect,
} from './organizationUnitRouteRedirects';

import { resolvePostLoginDestinationPath } from '@/services/auth/postLoginRedirectStorage';

const AdminOrgOnboardingLayout = lazy(
  () => import('pages/organizationFlow/AdminOrgOnboardingLayout')
);
const SelectOrganizationPage = lazy(
  () => import('pages/organizationFlow/routes/SelectOrganizationPage')
);
const CreateOrganizationPage = lazy(
  () => import('pages/organizationFlow/routes/CreateOrganizationPage')
);
const OrganizationSetupPage = lazy(
  () => import('pages/organizationFlow/routes/OrganizationSetupPage')
);
const ReviewUnitsPage = lazy(() => import('pages/organizationFlow/routes/ReviewUnitsPage'));
const SelectUnitPage = lazy(() => import('pages/organizationFlow/routes/SelectUnitPage'));
const CompleteSetupPage = lazy(() => import('pages/organizationFlow/routes/CompleteSetupPage'));
const ReviewPayrollPlanPage = lazy(
  () => import('pages/organizationFlow/routes/ReviewPayrollPlanPage')
);
const SendInvitePage = lazy(() => import('pages/organizationFlow/routes/SendInvitePage'));
const FinchPendingPage = lazy(() => import('pages/organizationFlow/routes/FinchPendingPage'));
const OrganizationListPage = lazy(() => import('pages/organizationFlow/organizationList'));
const OrganizationDetails = lazy(() => import('pages/organizationFlow/organizationDetails'));
const OrganizationUnitDetails = lazy(
  () => import('pages/organizationFlow/organizationUnitDetails')
);
const ReconnectPayrollPage = lazy(
  () => import('pages/organizationFlow/organizationDetails/ReconnectPayrollPage')
);
const MemberVerificationReviewPage = lazy(
  () => import('pages/organizationFlow/organizationUnitDetails/MemberVerificationReviewPage')
);
const AdminSignIn = lazy(() => import('pages/organizationFlow/entry/Login'));
const AdminOtpVerification = lazy(() => import('components/adminOtpVerification'));

const AdminSignInPage = () => {
  const navigate = useNavigate();

  const handleAdminLogin = () => {
    // Prefer a fresh MFA challenge over leftover tokens from a previous session
    // so a new admin can complete OTP instead of landing as the prior user.
    const challengeSession = getChallengeSession();
    if (challengeSession) {
      navigate(adminPaths.otpVerification, { replace: true });
      return;
    }

    const isAuthed = hasAuthTokens();
    if (isAuthed) {
      navigate(resolvePostLoginDestinationPath(adminPaths.onboarding, true), { replace: true });
      return;
    }

    navigate(adminPaths.signIn, { replace: true });
  };

  return <AdminSignIn onLogin={handleAdminLogin} />;
};

const AdminOtpPage = () => {
  return <AdminOtpVerification />;
};

const AdminRouteTree = (
  <>
    <Route element={<AdminPublicRoute />}>
      <Route path={adminPaths.signIn} element={<AdminSignInPage />} />
      <Route path={adminPaths.otpVerification} element={<AdminOtpPage />} />
    </Route>
    <Route element={<AdminPrivateRoute />}>
      <Route path={adminPaths.base} element={<Navigate to={adminPaths.onboarding} replace />} />
      <Route
        path={adminPaths.legacyOnboarding}
        element={<Navigate to={adminPaths.onboarding} replace />}
      />
      <Route path={orgOnboardingRoutePatterns.home} element={<AdminOrgOnboardingLayout />}>
        <Route index element={<SelectOrganizationPage />} />
        <Route path="new" element={<CreateOrganizationPage />} />
        <Route path=":orgName" element={<OrganizationSetupPage />} />
        <Route path=":orgName/units-review" element={<ReviewUnitsPage />} />
        <Route path=":orgName/select-unit" element={<SelectUnitPage />} />
        <Route path=":orgName/:unitId/payroll-plan" element={<CompleteSetupPage />} />
        <Route path=":orgName/:unitId/payroll-plan-review" element={<ReviewPayrollPlanPage />} />
        <Route path=":orgName/:unitId/send-invite" element={<SendInvitePage />} />
        <Route path=":orgName/:unitId/finch-pending" element={<FinchPendingPage />} />
      </Route>
      <Route path={adminPaths.organizationDashboard} element={<OrganizationListPage />} />
      <Route
        path={adminPaths.organizationList}
        element={<Navigate to={adminPaths.organizationDashboard} replace />}
      />
      <Route path={adminPaths.organizationUnits} element={<OrganizationDetails />} />
      <Route path={adminPaths.organizationUnitDetail} element={<OrganizationUnitDetails />} />
      <Route path={adminPaths.organizationReconnectPayroll} element={<ReconnectPayrollPage />} />
      <Route
        path={adminPaths.organizationMemberVerificationReview}
        element={<MemberVerificationReviewPage />}
      />
      <Route
        path={adminPaths.organizationUnitEmployees}
        element={<OrganizationUnitEmployeesPathRedirect />}
      />
      {/* Legacy routes */}
      <Route path={adminPaths.organization} element={<OrganizationDetails />} />
      <Route path={adminPaths.organizationUnit} element={<OrganizationUnitLegacyPathRedirect />} />
    </Route>
    <Route path="/admin/login" element={<Navigate to={adminPaths.signIn} replace />} />
  </>
);

export default AdminRouteTree;
